import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  Ban,
  Trash2,
  RefreshCw,
  Mail,
  Phone,
  Building2,
  Briefcase,
  MessageSquare,
  Globe,
  CalendarOff,
  CalendarDays,
  CalendarCheck2,
  CalendarClock,
  CalendarX2,
  Activity,
  UserPlus,
  Eye,
  ListTodo,
  Dot,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  BUSINESS_TZ,
  getSlotsForDate,
  isoCalendarDate,
  isoDateInBusinessTz,
  formatInTz,
} from "@/lib/booking";

// ─── Navy / Elevate palette (scoped to this view) ───────────────────────────
const C = {
  pageBg: "hsl(222 47% 6%)",
  surface: "hsl(222 45% 11%)",
  surfaceAlt: "hsl(222 42% 9%)",
  surfaceElev: "hsl(222 40% 14%)",
  border: "hsl(222 32% 20%)",
  borderStrong: "hsl(222 32% 28%)",
  textHi: "hsl(210 20% 98%)",
  text: "hsl(214 20% 88%)",
  textDim: "hsl(215 16% 68%)",
  textMute: "hsl(215 14% 50%)",
  accent: "hsl(207 92% 62%)",
  accentSoft: "hsl(207 92% 62% / 0.14)",
  accentRing: "hsl(207 92% 62% / 0.45)",
  success: "hsl(150 70% 50%)",
  successSoft: "hsl(150 70% 50% / 0.14)",
  danger: "hsl(0 78% 64%)",
  dangerSoft: "hsl(0 78% 64% / 0.14)",
  amber: "hsl(36 92% 62%)",
  amberSoft: "hsl(36 92% 62% / 0.14)",
  violet: "hsl(262 80% 70%)",
  violetSoft: "hsl(262 80% 70% / 0.14)",
};

const EGYPT_TZ = "Africa/Cairo";

interface Appointment {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  company: string | null;
  business_type: string | null;
  notes: string | null;
  slot_start: string;
  visitor_timezone: string;
  status: string;
  created_at: string;
}

interface BlockedSlot {
  id: string;
  blocked_date: string;
  slot_start: string | null;
}

interface ActivityItem {
  id: string;
  kind: "booking" | "lead_activity" | "visitor" | "task";
  title: string;
  subtitle?: string;
  at: string; // ISO
  color: string;
  icon: typeof Activity;
}

type RangeKey = "today" | "week" | "month";

const startOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const endOfDay = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
};
const startOfWeek = (d: Date) => {
  const x = startOfDay(d);
  const day = x.getDay(); // 0=Sun
  x.setDate(x.getDate() - day);
  return x;
};
const endOfWeek = (d: Date) => {
  const x = endOfDay(d);
  x.setDate(x.getDate() + (6 - x.getDay()));
  return x;
};
const startOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0, 0);
const endOfMonth = (d: Date) => new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999);

const relTime = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.round(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const AdminCalendar = () => {
  const [date, setDate] = useState<Date>(new Date());
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [blocked, setBlocked] = useState<BlockedSlot[]>([]);
  const [activity, setActivity] = useState<ActivityItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState<RangeKey>("week");

  const refresh = async () => {
    setLoading(true);
    const sinceIso = new Date(Date.now() - 1000 * 60 * 60 * 24 * 14).toISOString();
    const [a, b, leadAct, visEv, tasks] = await Promise.all([
      supabase.from("appointments").select("*").order("slot_start", { ascending: true }),
      supabase.from("blocked_slots").select("*"),
      supabase
        .from("lead_activities")
        .select("id, activity_type, title, body, created_at")
        .gte("created_at", sinceIso)
        .order("created_at", { ascending: false })
        .limit(15),
      supabase
        .from("visitor_events")
        .select("id, event_type, path, label, city, country, created_at")
        .in("event_type", ["session_started", "lead_converted", "high_intent"])
        .gte("created_at", sinceIso)
        .order("created_at", { ascending: false })
        .limit(15),
      supabase
        .from("lead_tasks")
        .select("id, title, due_at, completed_at")
        .is("completed_at", null)
        .not("due_at", "is", null)
        .order("due_at", { ascending: true })
        .limit(10),
    ]);
    if (a.data) setAppointments(a.data);
    if (b.data) setBlocked(b.data);

    // Build unified activity feed
    const feed: ActivityItem[] = [];
    (a.data ?? [])
      .filter((x) => x.status !== "cancelled")
      .sort((p, q) => new Date(q.created_at).getTime() - new Date(p.created_at).getTime())
      .slice(0, 8)
      .forEach((appt) =>
        feed.push({
          id: `bk-${appt.id}`,
          kind: "booking",
          title: `${appt.name} booked a call`,
          subtitle: formatInTz(new Date(appt.slot_start), BUSINESS_TZ, {
            month: "short",
            day: "numeric",
            hour: "numeric",
            minute: "2-digit",
          }) + " ET",
          at: appt.created_at,
          color: C.success,
          icon: CalendarCheck2,
        }),
      );
    (leadAct.data ?? []).forEach((la) =>
      feed.push({
        id: `la-${la.id}`,
        kind: "lead_activity",
        title: la.title || `Lead ${la.activity_type}`,
        subtitle: la.body ?? la.activity_type,
        at: la.created_at,
        color: C.accent,
        icon: UserPlus,
      }),
    );
    (visEv.data ?? []).forEach((v) =>
      feed.push({
        id: `ve-${v.id}`,
        kind: "visitor",
        title:
          v.event_type === "lead_converted"
            ? "Visitor converted"
            : v.event_type === "high_intent"
              ? "High-intent visit"
              : "New session",
        subtitle: [v.path, [v.city, v.country].filter(Boolean).join(", ")].filter(Boolean).join(" · "),
        at: v.created_at,
        color: C.violet,
        icon: Eye,
      }),
    );
    (tasks.data ?? []).forEach((t) =>
      feed.push({
        id: `tk-${t.id}`,
        kind: "task",
        title: t.title || "Follow-up",
        subtitle: t.due_at ? `Due ${new Date(t.due_at).toLocaleString()}` : "Due soon",
        at: t.due_at || new Date().toISOString(),
        color: C.amber,
        icon: ListTodo,
      }),
    );

    feed.sort((p, q) => new Date(q.at).getTime() - new Date(p.at).getTime());
    setActivity(feed.slice(0, 14));
    setLoading(false);
  };

  useEffect(() => {
    refresh();
  }, []);

  // ─── Derived data ────────────────────────────────────────────────────────
  const slotsForDay = useMemo(() => getSlotsForDate(date), [date]);
  const dayKey = isoCalendarDate(date);

  const blockedDateIds = useMemo(
    () => blocked.filter((b) => !b.slot_start).map((b) => b.blocked_date),
    [blocked],
  );
  const blockedDateSet = useMemo(() => new Set(blockedDateIds), [blockedDateIds]);
  const blockedSlotMap = useMemo(() => {
    const m = new Map<number, string>();
    blocked.forEach((b) => {
      if (b.slot_start) m.set(new Date(b.slot_start).getTime(), b.id);
    });
    return m;
  }, [blocked]);

  const bookedDateSet = useMemo(() => {
    const s = new Set<string>();
    appointments.forEach((a) => {
      if (a.status !== "cancelled") s.add(isoDateInBusinessTz(new Date(a.slot_start)));
    });
    return s;
  }, [appointments]);

  const apptByTime = useMemo(() => {
    const m = new Map<number, Appointment>();
    appointments.forEach((a) => {
      if (a.status !== "cancelled") m.set(new Date(a.slot_start).getTime(), a);
    });
    return m;
  }, [appointments]);

  const dayAppointments = useMemo(
    () => appointments.filter((a) => isoDateInBusinessTz(new Date(a.slot_start)) === dayKey),
    [appointments, dayKey],
  );

  const isDayBlocked = blockedDateSet.has(dayKey);

  // KPI range
  const { rangeStart, rangeEnd, rangeLabel } = useMemo(() => {
    const now = new Date();
    if (range === "today") return { rangeStart: startOfDay(now), rangeEnd: endOfDay(now), rangeLabel: "Today" };
    if (range === "week") return { rangeStart: startOfWeek(now), rangeEnd: endOfWeek(now), rangeLabel: "This week" };
    return { rangeStart: startOfMonth(now), rangeEnd: endOfMonth(now), rangeLabel: "This month" };
  }, [range]);

  const kpis = useMemo(() => {
    const live = appointments.filter((a) => a.status !== "cancelled");
    const inRange = live.filter((a) => {
      const t = new Date(a.slot_start).getTime();
      return t >= rangeStart.getTime() && t <= rangeEnd.getTime();
    });
    const upcoming = live.filter((a) => new Date(a.slot_start).getTime() >= Date.now());
    const todayCt = live.filter(
      (a) => isoDateInBusinessTz(new Date(a.slot_start)) === isoDateInBusinessTz(new Date()),
    ).length;
    return {
      rangeCount: inRange.length,
      upcoming: upcoming.length,
      today: todayCt,
      blocked: blockedDateIds.length,
    };
  }, [appointments, rangeStart, rangeEnd, blockedDateIds]);

  // ─── Actions ─────────────────────────────────────────────────────────────
  const toggleBlockDay = async () => {
    if (isDayBlocked) {
      const id = blocked.find((b) => !b.slot_start && b.blocked_date === dayKey)?.id;
      if (!id) return;
      const { error } = await supabase.from("blocked_slots").delete().eq("id", id);
      if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
      toast({ title: "Day unblocked" });
    } else {
      const { error } = await supabase.from("blocked_slots").insert({ blocked_date: dayKey });
      if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
      toast({ title: "Day blocked" });
    }
    refresh();
  };

  const toggleBlockSlot = async (s: Date) => {
    const existing = blockedSlotMap.get(s.getTime());
    if (existing) {
      const { error } = await supabase.from("blocked_slots").delete().eq("id", existing);
      if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      const { error } = await supabase
        .from("blocked_slots")
        .insert({ blocked_date: dayKey, slot_start: s.toISOString() });
      if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
    }
    refresh();
  };

  const cancelAppt = async (id: string) => {
    const { error } = await supabase.from("appointments").update({ status: "cancelled" }).eq("id", id);
    if (error) return toast({ title: "Error", description: error.message, variant: "destructive" });
    toast({ title: "Appointment cancelled" });
    refresh();
  };

  // ─── Render helpers ──────────────────────────────────────────────────────
  const Kpi = ({
    icon: Icon,
    label,
    value,
    accent,
    sub,
  }: {
    icon: typeof Activity;
    label: string;
    value: number | string;
    accent: string;
    sub?: string;
  }) => (
    <div
      className="relative overflow-hidden rounded-2xl border p-5 transition-colors"
      style={{ background: C.surface, borderColor: C.border }}
    >
      <div
        className="absolute inset-x-0 top-0 h-[2px]"
        style={{ background: accent }}
      />
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wider" style={{ color: C.textMute }}>
            {label}
          </p>
          <p className="mt-2 text-3xl font-bold tabular-nums tracking-tight" style={{ color: C.textHi }}>
            {value}
          </p>
          {sub && (
            <p className="mt-1 text-xs" style={{ color: C.textDim }}>
              {sub}
            </p>
          )}
        </div>
        <div
          className="flex h-10 w-10 items-center justify-center rounded-xl"
          style={{ background: `${accent.replace(")", " / 0.14)").replace("hsl(", "hsl(")}`, color: accent }}
        >
          <Icon size={18} />
        </div>
      </div>
    </div>
  );

  const QuickFilter = ({ k, label }: { k: RangeKey; label: string }) => {
    const active = range === k;
    return (
      <button
        onClick={() => setRange(k)}
        className="rounded-lg px-3 py-1.5 text-xs font-medium transition-colors"
        style={{
          background: active ? C.accentSoft : "transparent",
          color: active ? C.accent : C.textDim,
          border: `1px solid ${active ? C.accentRing : C.border}`,
        }}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-6" style={{ color: C.text }}>
      {/* ─── Header ─────────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight" style={{ color: C.textHi }}>
            Appointments Calendar
          </h2>
          <p className="mt-0.5 text-sm" style={{ color: C.textDim }}>
            Business hours 9 AM, 5 PM ET · 30-min slots · Live booking pipeline
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-1 rounded-xl border p-1"
            style={{ background: C.surfaceAlt, borderColor: C.border }}
          >
            <QuickFilter k="today" label="Today" />
            <QuickFilter k="week" label="This Week" />
            <QuickFilter k="month" label="This Month" />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={refresh}
            disabled={loading}
            style={{ borderColor: C.border, color: C.text, background: C.surface }}
          >
            <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* ─── KPI Row ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Kpi
          icon={CalendarDays}
          label={rangeLabel}
          value={kpis.rangeCount}
          accent={C.accent}
          sub={`${formatInTz(rangeStart, BUSINESS_TZ, { month: "short", day: "numeric" })}, ${formatInTz(rangeEnd, BUSINESS_TZ, { month: "short", day: "numeric" })}`}
        />
        <Kpi
          icon={CalendarCheck2}
          label="Today"
          value={kpis.today}
          accent={C.success}
          sub={kpis.today === 0 ? "No calls today" : `${kpis.today} call${kpis.today === 1 ? "" : "s"} scheduled`}
        />
        <Kpi
          icon={CalendarClock}
          label="Upcoming total"
          value={kpis.upcoming}
          accent={C.amber}
          sub="Across all future dates"
        />
        <Kpi
          icon={CalendarX2}
          label="Blocked days"
          value={kpis.blocked}
          accent={C.danger}
          sub="Off the booking grid"
        />
      </div>

      {/* ─── Main grid: Calendar | Day detail | Activity ───────────────── */}
      <div className="grid gap-5 xl:grid-cols-[320px_1fr_340px] lg:grid-cols-[300px_1fr]">
        {/* Calendar card */}
        <div
          className="rounded-2xl border p-4"
          style={{ background: C.surface, borderColor: C.border }}
        >
          <div className="mb-2 flex items-center justify-between px-1">
            <h3 className="text-sm font-semibold" style={{ color: C.textHi }}>
              Calendar
            </h3>
            <span className="text-[10px] font-medium uppercase tracking-wider" style={{ color: C.textMute }}>
              {date.toLocaleDateString("en-US", { month: "long", year: "numeric" })}
            </span>
          </div>
          <Calendar
            mode="single"
            selected={date}
            onSelect={(d) => d && setDate(d)}
            modifiers={{
              booked: (d) => bookedDateSet.has(isoCalendarDate(d)),
              blocked: (d) => blockedDateSet.has(isoCalendarDate(d)),
              today: (d) => isoCalendarDate(d) === isoDateInBusinessTz(new Date()),
            }}
            modifiersStyles={{
              booked: {
                fontWeight: 700,
                color: C.success,
                background: C.successSoft,
                borderRadius: 8,
              },
              blocked: {
                color: C.danger,
                background: C.dangerSoft,
                borderRadius: 8,
                textDecoration: "line-through",
              },
              today: {
                color: C.accent,
                background: C.accentSoft,
                borderRadius: 8,
                boxShadow: `inset 0 0 0 1px ${C.accentRing}`,
              },
            }}
            className="p-2 pointer-events-auto"
          />
          <div
            className="mt-3 grid grid-cols-2 gap-2 rounded-lg border p-3 text-[11px]"
            style={{ background: C.surfaceAlt, borderColor: C.border, color: C.textDim }}
          >
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.accent }} /> Today
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.success }} /> Booked
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm" style={{ background: C.danger }} /> Blocked
            </span>
            <span className="flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-sm border" style={{ borderColor: C.borderStrong }} /> Available
            </span>
          </div>
        </div>

        {/* Day detail card */}
        <div
          className="rounded-2xl border p-5"
          style={{ background: C.surface, borderColor: C.border }}
        >
          <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
            <div>
              <h3 className="text-base font-semibold" style={{ color: C.textHi }}>
                {date.toLocaleDateString("en-US", {
                  weekday: "long",
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </h3>
              <p className="mt-0.5 text-xs" style={{ color: C.textDim }}>
                Each slot in <strong style={{ color: C.text }}>US Eastern</strong> and{" "}
                <strong style={{ color: C.amber }}>Cairo</strong> time
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={toggleBlockDay}
              style={{
                borderColor: isDayBlocked ? C.danger : C.border,
                color: isDayBlocked ? C.danger : C.text,
                background: isDayBlocked ? C.dangerSoft : C.surfaceAlt,
              }}
            >
              <CalendarOff size={14} className="mr-1.5" />
              {isDayBlocked ? "Unblock day" : "Block whole day"}
            </Button>
          </div>

          <div className="space-y-1.5">
            {slotsForDay.map((s) => {
              const appt = apptByTime.get(s.getTime());
              const slotBlocked = blockedSlotMap.has(s.getTime());
              const past = s.getTime() < Date.now();

              // Visual state
              let bg = C.surfaceAlt;
              let bd = C.border;
              let leftBar = "transparent";
              if (appt) {
                bg = C.successSoft;
                bd = "hsl(150 70% 50% / 0.35)";
                leftBar = C.success;
              } else if (slotBlocked || isDayBlocked) {
                bg = C.dangerSoft;
                bd = "hsl(0 78% 64% / 0.3)";
                leftBar = C.danger;
              } else if (past) {
                bg = C.surfaceAlt;
                bd = C.border;
              }

              return (
                <div
                  key={s.getTime()}
                  className="relative flex items-center gap-3 overflow-hidden rounded-xl border p-3 text-sm transition-colors"
                  style={{
                    background: bg,
                    borderColor: bd,
                    opacity: past && !appt ? 0.55 : 1,
                  }}
                >
                  <span
                    className="absolute inset-y-0 left-0 w-1"
                    style={{ background: leftBar }}
                  />
                  <div className="ml-1 flex min-w-[150px] flex-col">
                    <span className="font-mono text-xs font-semibold" style={{ color: C.textHi }}>
                      {formatInTz(s, BUSINESS_TZ, { hour: "numeric", minute: "2-digit" })}
                      <span className="ml-1 text-[10px] font-normal" style={{ color: C.textMute }}>
                        ET
                      </span>
                    </span>
                    <span className="font-mono text-[11px]" style={{ color: C.amber }}>
                      {formatInTz(s, EGYPT_TZ, { hour: "numeric", minute: "2-digit" })} Cairo
                    </span>
                  </div>

                  <div className="flex-1 min-w-0">
                    {appt ? (
                      <div>
                        <div className="flex items-center gap-2">
                          <span
                            className="rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                            style={{ background: C.successSoft, color: C.success }}
                          >
                            Booked
                          </span>
                          <div className="truncate font-semibold" style={{ color: C.textHi }}>
                            {appt.name}
                          </div>
                        </div>
                        <div
                          className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px]"
                          style={{ color: C.textDim }}
                        >
                          <span className="flex items-center gap-1">
                            <Mail size={11} /> {appt.email}
                          </span>
                          {appt.phone && (
                            <span className="flex items-center gap-1">
                              <Phone size={11} /> {appt.phone}
                            </span>
                          )}
                          {appt.company && (
                            <span className="flex items-center gap-1">
                              <Building2 size={11} /> {appt.company}
                            </span>
                          )}
                          {appt.business_type && (
                            <span className="flex items-center gap-1">
                              <Briefcase size={11} /> {appt.business_type}
                            </span>
                          )}
                          <span className="flex items-center gap-1">
                            <Globe size={11} /> {appt.visitor_timezone}
                          </span>
                        </div>
                        {appt.notes && (
                          <div
                            className="mt-1 flex items-start gap-1 text-[11px]"
                            style={{ color: C.textDim }}
                          >
                            <MessageSquare size={11} className="mt-0.5 shrink-0" /> {appt.notes}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span
                        className="rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
                        style={{
                          background:
                            slotBlocked || isDayBlocked
                              ? C.dangerSoft
                              : past
                                ? "transparent"
                                : C.surfaceElev,
                          color:
                            slotBlocked || isDayBlocked
                              ? C.danger
                              : past
                                ? C.textMute
                                : C.textDim,
                        }}
                      >
                        {past ? "Past" : slotBlocked ? "Blocked" : isDayBlocked ? "Day blocked" : "Available"}
                      </span>
                    )}
                  </div>

                  <div className="flex gap-1">
                    {appt ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => cancelAppt(appt.id)}
                        style={{ borderColor: C.border, color: C.danger, background: C.surfaceAlt }}
                      >
                        <Trash2 size={12} />
                      </Button>
                    ) : (
                      !isDayBlocked &&
                      !past && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleBlockSlot(s)}
                          style={{
                            borderColor: C.border,
                            color: slotBlocked ? C.amber : C.textDim,
                            background: C.surfaceAlt,
                          }}
                        >
                          <Ban size={12} />
                        </Button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {dayAppointments.length === 0 && (
            <p className="mt-3 text-xs" style={{ color: C.textMute }}>
              No bookings on this day yet.
            </p>
          )}

          <p className="mt-4 text-[11px]" style={{ color: C.textMute }}>
            Bookings are stored in UTC and converted on display. Visitors always see times in their own
            local timezone.
          </p>
        </div>

        {/* Activity panel */}
        <div
          className="rounded-2xl border p-5 xl:col-auto lg:col-span-2 xl:col-span-1"
          style={{ background: C.surface, borderColor: C.border }}
        >
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg"
                style={{ background: C.accentSoft, color: C.accent }}
              >
                <Activity size={15} />
              </div>
              <div>
                <h3 className="text-sm font-semibold" style={{ color: C.textHi }}>
                  Recent Activity
                </h3>
                <p className="text-[11px]" style={{ color: C.textMute }}>
                  Last 14 days · bookings, leads, visitors
                </p>
              </div>
            </div>
          </div>

          {activity.length === 0 ? (
            <div
              className="rounded-xl border border-dashed p-6 text-center text-xs"
              style={{ borderColor: C.border, color: C.textMute }}
            >
              No recent activity yet. Bookings, lead updates, and high-intent visits will appear here.
            </div>
          ) : (
            <ul className="space-y-2">
              {activity.map((it) => {
                const Icon = it.icon;
                return (
                  <li
                    key={it.id}
                    className="flex gap-3 rounded-xl border p-3 transition-colors"
                    style={{ background: C.surfaceAlt, borderColor: C.border }}
                  >
                    <div
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg"
                      style={{
                        background: `${it.color.replace(")", " / 0.14)")}`,
                        color: it.color,
                      }}
                    >
                      <Icon size={14} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline justify-between gap-2">
                        <p
                          className="truncate text-[13px] font-medium"
                          style={{ color: C.textHi }}
                          title={it.title}
                        >
                          {it.title}
                        </p>
                        <span
                          className="shrink-0 text-[10px] font-medium tabular-nums"
                          style={{ color: C.textMute }}
                        >
                          {relTime(it.at)}
                        </span>
                      </div>
                      {it.subtitle && (
                        <p
                          className="mt-0.5 line-clamp-2 text-[11px]"
                          style={{ color: C.textDim }}
                          title={it.subtitle}
                        >
                          {it.subtitle}
                        </p>
                      )}
                      <div className="mt-1 flex items-center gap-1 text-[10px]" style={{ color: C.textMute }}>
                        <Dot size={12} style={{ color: it.color }} className="-ml-1" />
                        <span className="uppercase tracking-wider">{it.kind.replace("_", " ")}</span>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCalendar;
